import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { List } from 'immutable';
import classNames from 'classnames';
import { Carousel, Dropdown } from 'react-bootstrap';

import PictionJS from 'piction-js';
import ErrorHandler from '../ErrorHandler';
import UMOMediaView from '../UMOMediaView';
import UMODownloadView from '../UMODownloadView';

import piction from '../../utils/piction';
import appConfig from '../../appConfig';

import {
  nextSlide,
  previousSlide,
  setIndex,
  setMaxIndex,
} from '../../actions/umoCarousel';
import { useEffect } from 'react';

const mapStateToProps = (state) => ({
  umoCarousel: new Map(state.umoCarousel),
});

const mapDispatchToProps = (dispatch) => ({
  nextSlide: () => dispatch(nextSlide()),
  previousSlide: () => dispatch(previousSlide()),
  setIndex: (index) => dispatch(setIndex(index)),
  setMaxIndex: (maxIndex) => dispatch(setMaxIndex(maxIndex)),
  dispatch: (actionToDispatch) => dispatch(actionToDispatch),
});

// todo: refactor. remove isNotJustified, use classNameModifier -- i.e. same as workspace preview
function UMOCarouselView({
  umoList,
  umoCarousel,
  nextSlide,
  previousSlide,
  setMaxIndex,
  dispatch,
  setIndex,
}) {
  useEffect(() => {
    setIndex(0);
    setMaxIndex(umoList.size - 1);
    return () => {
      setIndex(0);
    };
  }, [umoList]);

  /**
   * This useEffect hook is responsible to focus out the media players, to make the arrow navigation work
   */
  useEffect(() => {
    const blurMediaOnFocus = (event) => {
      if (
        event.target.tagName &&
        ['audio', 'video'].includes(event.target.tagName.toLowerCase())
      ) {
        // Only blur if NOT in fullscreen
        const fullscreenElement =
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement;

        if (fullscreenElement !== event.target) {
          event.target.blur();
        }
      }
    };
    document.addEventListener('focusin', blurMediaOnFocus);

    return () => {
      document.removeEventListener('focusin', blurMediaOnFocus);
    };
  }, []);

  /**
   * This useEffect hook is responsible for handling keyboard navigation for the carousel.
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      if (fullscreenElement) return;
      if (event.key === 'ArrowLeft') {
        previousSlide();
      } else if (event.key === 'ArrowRight') {
        nextSlide();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const umoItems = [];
  const currentIndex = umoCarousel.get('currentIndex');
  for (let i = 0; i < umoList.size; i += 1) {
    const tempUMO = new PictionJS.UMO(umoList.get(i).toJS(), {
      hostURL: piction.hostURL,
    });
    const isRestricted =
      tempUMO.thumbnail &&
      tempUMO.thumbnail._data &&
      tempUMO.thumbnail._data.$.restricted === 'T';

    const titleMetatag =
      appConfig.umoTitleMetatag &&
      tempUMO.metadataList.getMetadata(appConfig.umoTitleMetatag) &&
      tempUMO.metadataList.getMetadata(appConfig.umoTitleMetatag).value;
    const title = titleMetatag || tempUMO.name;

    let fullImageFromTagValue = true;
    if (
      appConfig.advanced &&
      appConfig.advanced.zoomifyMetatag &&
      appConfig.advanced.zoomifyMetatag.tagName &&
      appConfig.advanced.zoomifyMetatag.tagValue
    ) {
      const configTag = appConfig.advanced.zoomifyMetatag;
      const umoTag =
        tempUMO.metadataList.getMetadata(configTag.tagName.toUpperCase()) &&
        tempUMO.metadataList.getMetadata(configTag.tagName.toUpperCase()).value;
      const tagValues = Array.isArray(configTag.tagValue)
        ? configTag.tagValue.map((val) => val.toUpperCase())
        : [configTag.tagValue.toUpperCase()];

      fullImageFromTagValue =
        umoTag && tagValues.includes(umoTag.toUpperCase());
    }

    // Allow fullscreen view for photos (web quality) or documents/records (html view)
    const fullImageType =
      (tempUMO.type === 'PHOTO' && tempUMO.webQuality) ||
      (!['PHOTO', 'AUDIO', 'VIDEO'].includes(tempUMO.type) &&
        tempUMO.originalList.all[0] &&
        tempUMO.originalList.all[0]._data.$.h);
    const tombstoneList = tempUMO.tombstoneMetadataList.all.map(
      (tombstoneItem, i) => (
        <p
          key={i}
          className={classNames('umo-grid-item-info__item', {
            'umo-grid-item-info__item--header': i === 0,
          })}
        >
          {appConfig.tombstoneCharacterLimit &&
          _.isNumber(appConfig.tombstoneCharacterLimit)
            ? _.truncate(tombstoneItem.value, {
                length: appConfig.tombstoneCharacterLimit,
              })
            : tombstoneItem.value}
        </p>
      )
    );
    umoItems.push(
      <Carousel.Item key={i}>
        <div className="umo-detail-view">
          <div className="umo-detail-view__image">
            {appConfig.umoDisplayTitleAboveImage ? (
              <div className="umo-detail-view__image-header umo-detail-view__image-header-title">
                {title}
              </div>
            ) : null}
            <div className="umo-detail-view__image-display">
              <div className="umo-detail-view__media">
                <ErrorHandler>
                  <UMOMediaView umo={tempUMO} carouselIndex={i} />
                </ErrorHandler>
              </div>
              <Dropdown
                id="dropdown-custom-2"
                className="umo-detail-view__image-display-download-image-button"
                variant="default"
                data-toggle="tooltip"
                data-placement="bottom"
              >
                <Dropdown.Toggle
                  variant="secondary"
                  size="sm"
                  disabled={
                    isRestricted ||
                    (tempUMO.originalList &&
                      tempUMO.originalList.all &&
                      tempUMO.originalList.all.length === 0)
                  }
                >
                  <i className="umo-detail-view__image-display-download-image-toggle" />
                  <i className="fas fa-caret-down" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <UMODownloadView
                    umo={tempUMO}
                    isRestricted={isRestricted}
                    dispatch={dispatch}
                  />
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
        <Carousel.Caption>{tombstoneList}</Carousel.Caption>
      </Carousel.Item>
    );
  }

  return (
    <div className="umo-gallery__carousel__outter">
      <Carousel
        interval={10000}
        indicators={false}
        activeIndex={currentIndex}
        controls={false}
      >
        {umoItems}
      </Carousel>
      <div className="umo-gallery__carousel__custom_arrow">
        <button
          onClick={() => previousSlide()}
          className="umo-gallery__carousel__custom_arrow--left"
        >
          <i className="fas fa-chevron-left" />
        </button>
        <button
          onClick={() => nextSlide()}
          className="umo-gallery__carousel__custom_arrow--right"
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>
    </div>
  );
}

UMOCarouselView.propTypes = {
  umoList: PropTypes.instanceOf(List).isRequired,
  nextSlide: PropTypes.func,
  previousSlide: PropTypes.func,
  setMaxIndex: PropTypes.func,
  umoCarousel: PropTypes.instanceOf(Map),
};

export default connect(mapStateToProps, mapDispatchToProps)(UMOCarouselView);
